import { getYearStructure } from './apiService';

interface AcademicYearStructure {
  academicYear: string;
  startWeek: number;
  endWeek: number;
  periods: any[];
  holidays: Holiday[];
}

interface WeekYearMapping {
  weekNumber: number;
  calendarYear: number;
  academicYear: string;
}

interface Holiday {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  type: string;
  description?: string | null;
}

// Cache for parsed academic year data to avoid repeated string parsing
interface ParsedAcademicYear {
  academicYear: string;
  startCalendarYear: number;
  endCalendarYear: number;
  startWeek: number;
  endWeek: number;
  weekToYearMap: Map<number, number>; // Precomputed week -> calendar year mapping
}

class AcademicYearService {
  private static instance: AcademicYearService;
  private cachedStructure: AcademicYearStructure | null = null;
  private parsedAcademicYear: ParsedAcademicYear | null = null; // Cache for parsed data
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private ongoingRequest: Promise<AcademicYearStructure | null> | null = null;

  private constructor() {}

  static getInstance(): AcademicYearService {
    if (!AcademicYearService.instance) {
      AcademicYearService.instance = new AcademicYearService();
    }
    return AcademicYearService.instance;
  }

  /**
   * Fetches and caches the current academic year structure
   * Prevents multiple concurrent requests by making subsequent calls wait for an in-flight request
   */
  async getAcademicYearStructure(): Promise<AcademicYearStructure | null> {
    const now = Date.now();
    
    // Return cached data if it's still fresh
    if (this.cachedStructure && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.cachedStructure;
    }

    // Wait for ongoing request if one exists
    if (this.ongoingRequest) {
      return await this.ongoingRequest;
    }

    // Start new request
    this.ongoingRequest = this.fetchYearStructureFromAPI();
    const result = await this.ongoingRequest;
    
    // Clear ongoing request flag
    this.ongoingRequest = null;
    
    // Cache the result and timestamp
    if (result) {
      this.cachedStructure = result;
      this.lastFetchTime = now;
      
      // Update parsed academic year cache
      this.updateParsedAcademicYear(result);
    }
    
    return result;
  }

  /**
   * Updates the parsed academic year cache with precomputed data
   * @param structure - The academic year structure
   */
  private updateParsedAcademicYear(structure: AcademicYearStructure): void {
    try {
      const [startYearStr] = structure.academicYear.split('-');
      if (!startYearStr) {
        console.warn(`[AcademicYearService] Invalid academic year format: ${structure.academicYear}`);
        this.parsedAcademicYear = null;
        return;
      }

      const startCalendarYear = parseInt(startYearStr, 10);
      if (isNaN(startCalendarYear)) {
        console.warn(`[AcademicYearService] Invalid start year in academic year: ${structure.academicYear}`);
        this.parsedAcademicYear = null;
        return;
      }

      const endCalendarYear = startCalendarYear + 1;

      // Precompute week-to-year mapping for all weeks (1-53)
      const weekToYearMap = new Map<number, number>();
      for (let week = 1; week <= 53; week++) {
        let calendarYear: number;
        
        // Handle normal academic year (e.g., start_week=1, end_week=52)
        if (structure.startWeek <= structure.endWeek) {
          calendarYear = startCalendarYear;
        } else {
          // Handle cross-year academic year (e.g., start_week=35, end_week=34)
          if (week >= structure.startWeek) {
            calendarYear = startCalendarYear; // First part of academic year
          } else if (week <= structure.endWeek) {
            calendarYear = endCalendarYear; // Second part of academic year
          } else {
            calendarYear = startCalendarYear; // Default to start year
          }
        }
        
        weekToYearMap.set(week, calendarYear);
      }

      this.parsedAcademicYear = {
        academicYear: structure.academicYear,
        startCalendarYear,
        endCalendarYear,
        startWeek: structure.startWeek,
        endWeek: structure.endWeek,
        weekToYearMap
      };
    } catch (error) {
      console.error('[AcademicYearService] Error updating parsed academic year:', error);
      this.parsedAcademicYear = null;
    }
  }

  /**
   * Private method that actually makes the API call
   */
  private async fetchYearStructureFromAPI(): Promise<AcademicYearStructure | null> {
    try {
      console.log('[AcademicYearService] Making API call to fetch year structure...');
      const structure = await getYearStructure() as AcademicYearStructure;
      
      // Update cache
      this.cachedStructure = structure;
      this.lastFetchTime = Date.now();
      
      console.log('[AcademicYearService] Year structure fetched and cached successfully');
      return structure;
    } catch (error) {
      console.warn('[AcademicYearService] Failed to fetch year structure:', error);
      return null;
    }
  }

  /**
   * Clears the cached academic year structure and cancels any ongoing requests
   */
  clearCache(): void {
    console.log('[AcademicYearService] Clearing cache and resetting state');
    this.cachedStructure = null;
    this.lastFetchTime = 0;
    this.ongoingRequest = null; // Clear any ongoing request reference
    this.parsedAcademicYear = null; // Clear parsed academic year cache
  }

  /**
   * Gets the parsed academic year data for optimal performance
   * @returns The parsed academic year data or null if not cached
   */
  getParsedAcademicYear(): ParsedAcademicYear | null {
    return this.parsedAcademicYear;
  }

  /**
   * Optimized version of mapWeekToCalendarYear that uses cached data
   * @param weekNumber - The week number (1-53)
   * @returns The calendar year for this week or null if not cached
   */
  mapWeekToCalendarYearOptimized(weekNumber: number): number | null {
    if (this.parsedAcademicYear && this.parsedAcademicYear.weekToYearMap.has(weekNumber)) {
      return this.parsedAcademicYear.weekToYearMap.get(weekNumber)!;
    }
    return null;
  }

  /**
   * Maps a week number to the correct calendar year based on academic year boundaries
   * @param weekNumber - The week number (1-53)
   * @param academicYear - The academic year string (e.g., "2025-2026")
   * @param academicStartWeek - The start week of the academic year
   * @param academicEndWeek - The end week of the academic year
   * @returns The calendar year for this week
   */
  static mapWeekToCalendarYear(
    weekNumber: number,
    academicYear: string,
    academicStartWeek: number,
    academicEndWeek: number
  ): number {
    // Cache current year for fallback scenarios (avoid multiple Date object creation)
    const currentYear = new Date().getFullYear();
    
    // Parse and validate academic year format using destructuring
    const [startYearStr] = academicYear.split('-');
    if (!startYearStr) {
      console.warn(`[AcademicYearService] Invalid academic year format: ${academicYear}`);
      return currentYear;
    }

    const startCalendarYear = parseInt(startYearStr, 10);
    if (isNaN(startCalendarYear)) {
      console.warn(`[AcademicYearService] Invalid start year in academic year: ${academicYear}`);
      return currentYear;
    }

    // Handle normal academic year (e.g., start_week=1, end_week=52)
    if (academicStartWeek <= academicEndWeek) {
      return startCalendarYear;
    }

    // Handle cross-year academic year (e.g., start_week=35, end_week=34)
    // Weeks >= start_week belong to the start calendar year
    // Weeks <= end_week belong to the next calendar year
    if (weekNumber >= academicStartWeek) {
      return startCalendarYear; // First part of academic year
    } else if (weekNumber <= academicEndWeek) {
      return startCalendarYear + 1; // Second part of academic year
    }

    // Week is outside academic year bounds, default to start year
    // console.warn(`[AcademicYearService] Week ${weekNumber} is outside academic year bounds ${academicStartWeek}-${academicEndWeek}`);
    return startCalendarYear;
  }

  /**
   * Gets the calendar year for a specific week within the current academic year structure
   * @param weekNumber - The week number (1-53)
   * @returns Promise<number> - The calendar year for this week
   */
  async getCalendarYearForWeek(weekNumber: number): Promise<number> {
    const structure = await this.getAcademicYearStructure();
    
    if (!structure) {
      // Fallback: assume current calendar year if no structure available
      return new Date().getFullYear();
    }

    return AcademicYearService.mapWeekToCalendarYear(
      weekNumber,
      structure.academicYear,
      structure.startWeek,
      structure.endWeek
    );
  }

  /**
   * Checks if a week number is within the academic year bounds
   * @param weekNumber - The week number to check
   * @param academicStartWeek - Start week of academic year
   * @param academicEndWeek - End week of academic year
   * @returns boolean - True if week is within bounds
   */
  static isWeekWithinAcademicYear(
    weekNumber: number,
    academicStartWeek: number,
    academicEndWeek: number
  ): boolean {
    // Handle normal academic year (e.g., start_week=1, end_week=52)
    if (academicStartWeek <= academicEndWeek) {
      return weekNumber >= academicStartWeek && weekNumber <= academicEndWeek;
    }

    // Handle cross-year academic year (e.g., start_week=36, end_week=35)
    // This means weeks 36-53 and weeks 1-35 are valid
    return weekNumber >= academicStartWeek || weekNumber <= academicEndWeek;
  }

  /**
   * Gets all week-to-year mappings for the current academic year
   * @returns Promise<WeekYearMapping[]> - Array of week-year mappings
   */
  async getAllWeekYearMappings(): Promise<WeekYearMapping[]> {
    const structure = await this.getAcademicYearStructure();
    
    if (!structure) {
      // Fallback: return all weeks mapped to current year
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 53 }, (_, i) => ({
        weekNumber: i + 1,
        calendarYear: currentYear,
        academicYear: `${currentYear}-${currentYear + 1}`
      }));
    }

    const mappings: WeekYearMapping[] = [];
    
    for (let week = 1; week <= 53; week++) {
      if (AcademicYearService.isWeekWithinAcademicYear(week, structure.startWeek, structure.endWeek)) {
        const calendarYear = AcademicYearService.mapWeekToCalendarYear(
          week,
          structure.academicYear,
          structure.startWeek,
          structure.endWeek
        );
        
        mappings.push({
          weekNumber: week,
          calendarYear,
          academicYear: structure.academicYear
        });
      }
    }

    return mappings;
  }

  /**
   * Validates that a week series is within academic year bounds
   * @param weekSeries - The week series string (e.g., "1-5,10,15-20")
   * @returns Promise<{isValid: boolean, invalidWeeks: number[]}> - Validation result
   */
  async validateWeekSeries(weekSeries: string): Promise<{isValid: boolean, invalidWeeks: number[]}> {
    const structure = await this.getAcademicYearStructure();
    
    if (!structure) {
      return { isValid: true, invalidWeeks: [] }; // Can't validate without structure
    }

    const invalidWeeks: number[] = [];
    
    // Parse week series to get individual week numbers
    const weekNumbers = this.parseWeekSeries(weekSeries);
    
    for (const weekNumber of weekNumbers) {
      if (!AcademicYearService.isWeekWithinAcademicYear(weekNumber, structure.startWeek, structure.endWeek)) {
        invalidWeeks.push(weekNumber);
      }
    }

    return {
      isValid: invalidWeeks.length === 0,
      invalidWeeks
    };
  }

  /**
   * Checks if a specific date is a holiday
   * @param date - The date to check
   * @returns Promise<Holiday | null> - The holiday if the date is a holiday, null otherwise
   */
  async isHoliday(date: Date): Promise<Holiday | null> {
    const structure = await this.getAcademicYearStructure();
    
    if (!structure || !structure.holidays || structure.holidays.length === 0) {
      return null;
    }

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0); // Normalize to start of day

    for (const holiday of structure.holidays) {
      const startDate = new Date(holiday.startDate);
      const endDate = new Date(holiday.endDate);
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      if (targetDate >= startDate && targetDate <= endDate) {
        return holiday;
      }
    }

    return null;
  }

  /**
   * Gets all holidays for a specific week
   * @param weekNumber - The week number
   * @param year - The calendar year
   * @returns Promise<Holiday[]> - Array of holidays in that week
   */
  async getHolidaysForWeek(weekNumber: number, year: number): Promise<Holiday[]> {
    const structure = await this.getAcademicYearStructure();
    
    if (!structure || !structure.holidays || structure.holidays.length === 0) {
      return [];
    }

    // Calculate the start and end of the week
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

    const weekStart = new Date(week1Monday);
    weekStart.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const holidaysInWeek: Holiday[] = [];

    for (const holiday of structure.holidays) {
      const holidayStart = new Date(holiday.startDate);
      const holidayEnd = new Date(holiday.endDate);
      
      // Check if holiday overlaps with the week
      if (holidayStart <= weekEnd && holidayEnd >= weekStart) {
        holidaysInWeek.push(holiday);
      }
    }

    return holidaysInWeek;
  }

  /**
   * Gets holiday information for a specific day of the week within a given week
   * @param dayOfWeek - The day name (e.g., 'monday', 'tuesday')
   * @param weekNumber - The week number
   * @param year - The calendar year
   * @returns Promise<Holiday | null> - The holiday if the day is a holiday, null otherwise
   */
  async getHolidayForDay(dayOfWeek: string, weekNumber: number, year: number): Promise<Holiday | null> {
    const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
      .indexOf(dayOfWeek.toLowerCase());
    
    if (dayIndex === -1) {
      console.warn(`[AcademicYearService] Invalid day of week: ${dayOfWeek}`);
      return null;
    }

    // Calculate the specific date for this day
    const jan4 = new Date(year, 0, 4);
    const dayOfWeekJan4 = jan4.getDay() || 7;
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - dayOfWeekJan4 + 1);

    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

    const targetDate = new Date(targetMonday);
    if (dayIndex === 0) {
      // Sunday
      targetDate.setDate(targetMonday.getDate() + 6);
    } else {
      // Monday (1) to Saturday (6)
      targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
    }

    return await this.isHoliday(targetDate);
  }

  /**
   * Parses a week series string into individual week numbers
   * @param weekSeries - The week series string (e.g., "1-5,10,15-20")
   * @returns number[] - Array of week numbers
   */
  private parseWeekSeries(weekSeries: string): number[] {
    if (!weekSeries || weekSeries.trim() === '') {
      return [];
    }

    const weekNumbers = new Set<number>();
    const parts = weekSeries.split(',');

    for (const part of parts) {
      const trimmedPart = part.trim();
      if (trimmedPart.includes('-')) {
        const rangeParts = trimmedPart.split('-');
        if (rangeParts.length === 2 && rangeParts[0] && rangeParts[1]) {
          const start = parseInt(rangeParts[0], 10);
          const end = parseInt(rangeParts[1], 10);

          if (!isNaN(start) && !isNaN(end)) {
            if (start <= end) {
              // Normal range
              for (let i = start; i <= end; i++) {
                weekNumbers.add(i);
              }
            } else {
              // Cross-year range like "52-4"
              for (let i = start; i <= 53; i++) {
                weekNumbers.add(i);
              }
              for (let i = 1; i <= end; i++) {
                weekNumbers.add(i);
              }
            }
          } else {
            console.warn(`[AcademicYearService] Invalid week numbers in range: ${trimmedPart}`);
          }
        } else {
          console.warn(`[AcademicYearService] Invalid range format: ${trimmedPart}`);
        }
      } else {
        const weekNum = parseInt(trimmedPart, 10);
        if (!isNaN(weekNum)) {
          weekNumbers.add(weekNum);
        }
      }
    }

    return Array.from(weekNumbers).sort((a, b) => a - b);
  }
}

export default AcademicYearService;
export type { AcademicYearStructure, WeekYearMapping, Holiday }; 